import { Link } from 'react-router-dom';

export default function ProfileSection() {
  return (
    <section id="profile" className="py-32 bg-white relative overflow-hidden">
      {/* Elegant background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-20 w-48 h-48 border border-amber-400 rounded-full"></div>
        <div className="absolute bottom-20 left-20 w-32 h-32 border border-amber-400 rounded-full"></div>
      </div>

      <div className="max-w-7xl mx-auto px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center mb-8">
            <div className="w-20 h-px bg-amber-400"></div>
            <span 
              className="mx-8 text-amber-600 text-sm tracking-[0.3em] font-light uppercase"
              style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
            >
              Profile Details
            </span>
            <div className="w-20 h-px bg-amber-400"></div>
          </div>
          
          <h2 
            className="text-5xl md:text-6xl font-light text-gray-900 mb-6 tracking-wide"
            style={{ 
              fontFamily: '"Noto Serif JP", serif',
              letterSpacing: '0.02em'
            }}
          >
            執事ベル
          </h2>
          
          <p 
            className="text-xl text-amber-600 font-light"
            style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
          >
            全日本執事協会 頭取 / Butler Professional
          </p>
        </div>

        {/* 2-Column Layout */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Image */}
          <div className="relative">
            <div className="relative group">
              {/* Main image container */}
              <div className="relative overflow-hidden shadow-2xl">
                <img
                  src="/images/gakken/gk-15.jpg"
                  alt="執事ベル（石井裕一）- プロフェッショナルポートレート"
                  className="w-full h-auto object-cover object-top"
                />
                
                {/* Elegant overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
              
              {/* Decorative frame */}
              <div className="absolute -inset-4 border-2 border-amber-400/30 transform rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
              <div className="absolute -inset-6 border border-amber-400/20 transform -rotate-1 group-hover:rotate-0 transition-transform duration-700"></div>
            </div>

            {/* Floating credentials */}
            <div className="absolute -bottom-8 -right-8 bg-white shadow-xl p-6 border-l-4 border-amber-400">
              <div 
                className="text-sm text-amber-700 font-medium tracking-wide mb-1"
                style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
              >
                全日本執事協会
              </div>
              <div 
                className="text-xs text-gray-600"
                style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
              >
                頭取
              </div>
            </div>
          </div>

          {/* Right Column - Content */}
          <div className="space-y-8">
            {/* Philosophy and Vision */}
            <div>
              <h3 
                className="text-3xl font-light text-gray-900 mb-6 tracking-wide"
                style={{ 
                  fontFamily: '"Noto Serif JP", serif',
                  letterSpacing: '0.02em'
                }}
              >
                理念とビジョン
              </h3>
              
              <div className="space-y-6">
                <p 
                  className="text-lg text-gray-700 leading-relaxed font-light"
                  style={{ 
                    fontFamily: '"Noto Sans JP", sans-serif',
                    lineHeight: '1.9'
                  }}
                >
                  全日本執事協会頭取として、日本における執事文化の普及と発展に取り組んでいます。執事という職業が持つ本質的な価値—相手を思いやる心と洗練された所作—を大切にしながら、現代社会に適した新しい執事像を追求しています。
                </p>
                
                <p 
                  className="text-lg text-gray-700 leading-relaxed font-light"
                  style={{ 
                    fontFamily: '"Noto Sans JP", sans-serif',
                    lineHeight: '1.9'
                  }}
                >
                  西洋の伝統的な執事文化と日本固有のおもてなし精神を融合させた、独自のサービススタイルの確立を目指しています。執事文化の研究と実践を通じて日々学び続けている知見を、皆様と共有し、共に成長できる場を提供することを大切にしています。
                </p>
              </div>
            </div>

            {/* Focus Areas */}
            <div>
              <h4 
                className="text-2xl font-light text-gray-900 mb-6 tracking-wide"
                style={{ 
                  fontFamily: '"Noto Serif JP", serif',
                  letterSpacing: '0.02em'
                }}
              >
                取り組んでいること
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  "執事文化の普及",
                  "マナー教育",
                  "ホスピタリティ研究", 
                  "サービス品質向上",
                  "人材育成支援",
                  "おもてなしの探求"
                ].map((expertise, index) => (
                  <div 
                    key={index}
                    className="flex items-center p-3 bg-gray-50 hover:bg-amber-50 transition-colors duration-300 border-l-2 border-amber-400/30 hover:border-amber-400"
                  >
                    <i className="ri-check-line text-amber-600 mr-3"></i>
                    <span 
                      className="text-gray-700 font-light"
                      style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                    >
                      {expertise}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Philosophy */}
            <div className="bg-gray-50 p-8 border-l-4 border-amber-400">
              <h4 
                className="text-xl font-light text-gray-900 mb-4 tracking-wide"
                style={{ 
                  fontFamily: '"Noto Serif JP", serif',
                  letterSpacing: '0.02em'
                }}
              >
                大切にしていること
              </h4>
              
              <blockquote 
                className="text-lg text-gray-700 leading-relaxed font-light italic"
                style={{ 
                  fontFamily: '"Noto Sans JP", sans-serif',
                  lineHeight: '1.8'
                }}
              >
                「真のエレガンスとは、相手への深い思いやりと、洗練された所作の調和から生まれるもの。一人ひとりのお客様に心を込めて向き合い、期待を超える体験をお届けすることを目指しています。」
              </blockquote>
            </div>

            {/* Contact CTA */}
            <div className="pt-6">
              <Link
                to="/contact"
                className="group relative inline-flex items-center bg-amber-700 hover:bg-amber-800 text-white px-10 py-4 font-light text-lg transition-all duration-500 overflow-hidden whitespace-nowrap"
                style={{ 
                  fontFamily: '"Noto Sans JP", sans-serif',
                  letterSpacing: '0.1em'
                }}
              >
                <span className="relative z-10 mr-3">お問い合わせ</span>
                <i className="ri-arrow-right-line relative z-10 text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
                
                {/* Hover effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-amber-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Section divider */}
      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-40 h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent"></div>
    </section>
  );
}
